import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for we

// Import Reducers
import authReducer from "client/slices/authSlice";
import languageReducer from "client/slices/languageSlice";
import cartReducer from "client/slices/cartSlice";
import insightDrawerReducer from "portal/slices/insightDrawerSlice";
import assistantReducer from "portal/slices/assistantSlice";
import patientDrawerReducer from "portal/slices/patientDrawerSlice";
import { queryClient } from "queryClient";
import { clearPredictiveCache } from "portal/hooks/usePredictive";
import { clearClinicalCache } from "portal/hooks/useClinical";

// Combine multiple reducers
const appReducer = combineReducers({
  auth: authReducer,
  language: languageReducer,
  cart: cartReducer,
  insightDrawer: insightDrawerReducer,
  assistant: assistantReducer,
  patientDrawer: patientDrawerReducer,
});

// Root reducer: on logout, wipe entire state + clear persistence
const rootReducer = (state, action) => {
  if (action.type === "auth/logout") {
    storage.removeItem("persist:root");
    localStorage.removeItem("token");
    state = undefined;
  }
  return appReducer(state, action);
};

// Persist configuration
const persistConfig = {
  key: "root",
  storage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// React Query's cache keys (e.g. 'dashboardCounts') and the hand-rolled
// module-scope caches in usePredictive.js/useClinical.js don't carry user/
// clinic/demo identity, so cached responses from one identity would otherwise
// be served instantly to the next (demo → real login, clinic A → clinic B
// impersonation, etc.) until a hard refresh. Wipe all of them on every auth boundary.
const clearQueryCacheOnAuthChange = () => (next) => (action) => {
  if (
    action.type === "auth/login" ||
    action.type === "auth/logout" ||
    action.type === "auth/startImpersonation"
  ) {
    queryClient.clear();
    clearPredictiveCache();
    clearClinicalCache();
  }
  return next(action);
};

// ─────────────────────────────────────────────────────────────────────────────
// DEMO PREVIEW STORE (?demo=1)
// Used only by the hero-laptop preview iframes. It is fully ISOLATED:
//   • non-persisted (noop storage) → never reads/writes localStorage, so it can
//     never pollute or log out a real user who happens to view the landing page
//   • pre-authenticated as a demo account so portal pages render (no redirect/
//     recursion), and the API layer serves mock data (see mockConfig.isDemoMode)
// ─────────────────────────────────────────────────────────────────────────────
const isDemoUrl =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("demo") === "1";

const noopStorage = {
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
};

const DEMO_AUTH = {
  isAuthenticated: true,
  user: {
    UserId: 99, UserName: "Demo Admin", FirstName: "Demo", LastName: "Admin",
    Email: "demo@vistaar.com", RoleId: 2, clinic_id: 1, is_demo: 1,
    ClinicName: "Demo Therapy Center",
  },
  token: "demo-preview",
  refreshToken: null,
  permissions: [
    "view_employees", "view_finance", "view_analytics", "view_income_expense",
    "view_patients", "view_appointments", "view_services", "view_attendance",
  ],
  modules: [],
  clinic_id: 1,
  clinicBranding: null,
  isImpersonating: false,
  impersonatedClinicName: null,
  ownedClinics: [],
};

// Function to create store
export const createStore = () => {
  if (isDemoUrl) {
    if (typeof window !== "undefined") window.__VISTAAR_DEMO__ = true;
    const demoReducer = persistReducer({ key: "root", storage: noopStorage }, rootReducer);
    const store = configureStore({
      reducer: demoReducer,
      preloadedState: { auth: DEMO_AUTH },
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false }).concat(clearQueryCacheOnAuthChange),
    });
    const persistor = persistStore(store);
    return { store, persistor };
  }

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false, // Disables serialization warnings from redux-persist
      }).concat(clearQueryCacheOnAuthChange),
  });

  const persistor = persistStore(store);
  return { store, persistor };
};

// Default export
export default createStore;
